import { useState, useEffect } from "react";
import RightTimeApi from "@/API";
import { FaArrowCircleLeft } from "react-icons/fa";

export default function CompanyOrgChartTab(props: any) {
  const { updateSpinner, activeTab, adminPerms, company } = props.tabData;

  const [data, updateData] = useState<any>({});
  const [orgChart, setOrgChart] = useState([]);
  const [positionChart, setPositionChart] = useState([]);
  const [positionChartWithPeople, setPositionChartWithPeople] = useState([]);

  useEffect(() => {
    getDataAsync();
  }, [activeTab]);

  function isOnThisTab() {
    return props && activeTab && activeTab === "orgchart";
  }
  function buildPositionChart(positions) {
    const positionMap = {};
    const tree = [];

    // Create a map of positions by their positionId
    positions.forEach((position) => {
      positionMap[position.positionId] = { ...position, subordinatePositions: [] };
    });

    // Populate the subordinate positions for each position
    positions.forEach((position) => {
      if (position.supervisorPositionId) {
        if (positionMap[position.supervisorPositionId]) {
          positionMap[position.supervisorPositionId].subordinatePositions.push(positionMap[position.positionId]);
        }
      }
    });

    // Identify top-level positions (no supervisor)
    positions.forEach((position) => {
      if (!position.supervisorPositionId) {
        tree.push(positionMap[position.positionId]);
      }
    });

    return tree;
  }

  function buildPositionChartWithPeople(positions, members) {
    const positionMap = {};
    const tree = [];

    // Create a map of positions by their positionId
    positions.forEach((position) => {
      positionMap[position.positionId] = { ...position, subordinatePositions: [], assignedUsers: [] };
    });

    // Assign users to their positions
    members.forEach((member) => {
      if (member.positionId && positionMap[member.positionId]) {
        positionMap[member.positionId].assignedUsers.push(member);
      }
    });

    // Populate the subordinate positions for each position
    positions.forEach((position) => {
      if (position.supervisorPositionId) {
        if (positionMap[position.supervisorPositionId]) {
          positionMap[position.supervisorPositionId].subordinatePositions.push(positionMap[position.positionId]);
        }
      }
    });

    // Identify top-level positions (no supervisor)
    positions.forEach((position) => {
      if (!position.supervisorPositionId) {
        tree.push(positionMap[position.positionId]);
      }
    });

    return tree;
  }

  function buildOrgChart(members) {
    const memberMap = {};
    const tree = [];
    const nonHierarchical = [];

    // Create a map of members by their userId
    members.forEach((member) => {
      memberMap[member.userId] = { ...member, subordinates: [] };
    });

    // Populate the subordinates for each member
    members.forEach((member) => {
      if (member.supervisorUserId) {
        if (memberMap[member.supervisorUserId]) {
          memberMap[member.supervisorUserId].subordinates.push(memberMap[member.userId]);
        }
      }
    });

    // Identify top-level members and non-hierarchical users
    members.forEach((member) => {
      const hasSubordinates = memberMap[member.userId]?.subordinates.length > 0;
      const hasSupervisor = !!member.supervisorUserId;

      if (!hasSupervisor && hasSubordinates) {
        // Add to the tree if they are top-level members (no supervisor but have subordinates)
        tree.push(memberMap[member.userId]);
      } else if (!hasSupervisor && !hasSubordinates) {
        // Add to non-hierarchical if they have no supervisor and no subordinates
        nonHierarchical.push(member);
      }
    });

    return { tree, nonHierarchical };  }
  function PeoplePositionNode({ position }) {
    const [isDragOver, setIsDragOver] = useState(false);

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      handleDropOnPosition(e, position);
    };

    return (
      <div 
        className={`positionNode peoplePosition ${isDragOver ? 'drag-over' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="positionContent">
          <div className="positionInfo">
            <div className="positionTitle">{position.title}</div>            {position.assignedUsers && position.assignedUsers.length > 0 ? (
              <div className="assignedUsers">
                {position.assignedUsers.map((user, index) => (
                  <div 
                    key={user.userId} 
                    className="assignedUser"
                    draggable
                    onDragStart={(e) => handleDragStartUser(e, user)}
                  >
                    {user.firstName} {user.lastName}
                  </div>
                ))}
              </div>
            ) : (
              <div className="emptyPosition">No one assigned</div>
            )}
          </div>
          {position.subordinatePositions && position.subordinatePositions.length > 0 && (
            <div className="subordinatePositions">
              {position.subordinatePositions.map((subPosition) => (
                <PeoplePositionNode key={subPosition.positionId} position={subPosition} />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  function PositionNode({ position }) {
    const [isDragOver, setIsDragOver] = useState(false);

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      handleDropOnPosition(e, position);
    };

    return (
      <div
        className={`positionNode ${isDragOver ? 'drag-over' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="positionContent">
          <div className="positionInfo">
            <div className="positionTitle">{position.title}</div>
            {position.assignedUser && (
              <div className="assignedUser">
                {position.assignedUser.firstName} {position.assignedUser.lastName}
              </div>
            )}
          </div>
          {position.subordinatePositions && position.subordinatePositions.length > 0 && (
            <div className="subordinatePositions">
              {position.subordinatePositions.map((subPosition) => (
                <PositionNode key={subPosition.positionId} position={subPosition} />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  function OrgChartNode({ member }) {
    return (
      <div
        className="orgChartNode"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => handleDropOnNode(e, member)}
        draggable
        onDragStart={(e) => handleDragStartUser(e, member)}
      >
        <div className="orgChartContent">
          <div className="memberInfo">
            {member.firstName} {member.lastName}
            {member.title && <div className="title">{member.title}</div>}
          </div>
          {member.subordinates.length > 0 && (
            <div className="subordinates">
              {member.subordinates.map((sub) => (
                <OrgChartNode key={sub.userId} member={sub} />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }
  async function handleDropOnPosition(event, position) {
    event.preventDefault();
    event.stopPropagation();
    const droppedUser = JSON.parse(event.dataTransfer.getData("text/plain"));
    
    try {
      updateSpinner(true);
      const resp = await RightTimeApi.putAsync(`/CompanyUser/${droppedUser.userId}/Job/${position.positionId}`);
      updateSpinner(false);
      
      if (resp && resp.ok) {
        console.log(`Successfully assigned user ${droppedUser.firstName} ${droppedUser.lastName} to position ${position.title}`);
        await getDataAsync(); // Refresh the org chart
      } else {
        console.error('Failed to assign user to position');
      }
    } catch (error) {
      updateSpinner(false);
      console.error('Error assigning user to position:', error);
    }
  }

  async function handleDropOnNode(event, supervisor) {
    event.preventDefault();
    event.stopPropagation();
    const droppedUser = JSON.parse(event.dataTransfer.getData("text/plain"));
    const resp = await RightTimeApi.putAsync("/User/" + droppedUser.userId + "/Supervisor/" + supervisor.userId);
    if (resp && resp.ok) {
      await getDataAsync(); // Refresh the org chart
    }
  }

  async function onRemoveFromPosition(event) {
    event.preventDefault();
    event.stopPropagation();
    const droppedUser = JSON.parse(event.dataTransfer.getData("text/plain"));
    
    try {
      updateSpinner(true);
      // Remove user from position by setting job to null/empty
      const resp = await RightTimeApi.putAsync(`/CompanyUser/${droppedUser.userId}/Job/`);
      updateSpinner(false);
      
      if (resp && resp.ok) {
        console.log(`Successfully removed user ${droppedUser.firstName} ${droppedUser.lastName} from position`);
        await getDataAsync(); // Refresh the org chart
      } else {
        console.error('Failed to remove user from position');
      }
    } catch (error) {
      updateSpinner(false);
      console.error('Error removing user from position:', error);
    }
  }

  async function onRemoveSupervisor(event) {
    event.preventDefault();
    event.stopPropagation();
    const droppedUser = JSON.parse(event.dataTransfer.getData("text/plain"));
    const resp = await RightTimeApi.putAsync("/User/" + droppedUser.userId + "/Supervisor/" + 0);
    if (resp && resp.ok) {
      await getDataAsync(); // Refresh the org chart
    }
  }

  const [nonHierarchicalUsers, setNonHierarchicalUsers] = useState([]);

  async function getDataAsync() {
    if (isOnThisTab()) {
      if (company?.id) {
        updateSpinner(true);
        const resp = await RightTimeApi.getAsync("/CompanyOrgMembers/" + company.id);
        updateSpinner(false);
        if (resp && resp.ok && resp.jsonData) {
          updateData(resp.jsonData);
          
          // Build position-based org chart
          if (resp.jsonData.positions) {
            const positionTree = buildPositionChart(resp.jsonData.positions);
            setPositionChart(positionTree);
            
            // Build position chart with people assigned
            if (resp.jsonData.members) {
              const positionTreeWithPeople = buildPositionChartWithPeople(resp.jsonData.positions, resp.jsonData.members);
              setPositionChartWithPeople(positionTreeWithPeople);
            }
          }
          
          // Build people-based org chart (legacy)
          if (resp.jsonData.members) {
            const { tree, nonHierarchical } = buildOrgChart(resp.jsonData.members);
            setOrgChart(tree);
            
            // Filter unassigned users to only show those without a positionId
            const unassignedUsers = resp.jsonData.members.filter(member => !member.positionId);
            setNonHierarchicalUsers(unassignedUsers);
          }
        }
      }
    }
  }

  const potentials = data?.potentials?.sort((a, b) => a.lastName.localeCompare(b.lastName));


  function handleDragStartUser(event, user) {
    event.stopPropagation(); // Prevent triggering the parent drag event
    event.dataTransfer.setData("text/plain", JSON.stringify(user));
  }


  // if (!orgChart.length) return null;
  if (isOnThisTab()) {
    return (
      <div className="orgChartPage">
        <div className="orgChartPanel">
          <div className="functionalOrgChart">
            <h3>Functional Organizational Chart</h3>
            {positionChart.map((position) => (
              <PositionNode key={position.positionId} position={position} />
            ))}
          </div>
            <div className="peopleOrgChart">
            <h3>People-Based Organizational Chart</h3>
            {positionChartWithPeople.map((position) => (
              <PeoplePositionNode key={position.positionId} position={position} />
            ))}
          </div>
        </div>
        <div className="unassignedUsersPanel">
          <div className="unassignedUsers">          <h3
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => onRemoveFromPosition(e)}
          >
            Unassigned Users
          </h3>
          <ul>
            {nonHierarchicalUsers.map((member) => (
              <li
                key={member.userId}
                draggable
                onDragStart={(e) => handleDragStartUser(e, member)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => handleDropOnNode(e, member)}
              >
                <div className="memberInfo">
                  {member.firstName} {member.lastName}
                  {member.title && <div className="title">{member.title}</div>}
                </div>
              </li>
            ))}
          </ul>
          </div>
        </div>
      </div>
    );
  }
}
